import { useCallback } from 'react'

import { UAParser } from 'ua-parser-js'

import type { Context } from './commonSchema'

import { useConfigProperties } from 'hooks/useEsConfig'
import { getDeviceID } from 'storage/local-storage'
import getEnv from 'utils/getEnv'

const getTimeZone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

const _resultPromise = new UAParser().getResult().withClientHints()

/**
 * Get the user-agent, either from navigator.userAgentData or
 * navigator.userAgent.
 */
export const getPlatform = async (): Promise<{
  platform: string | undefined
  platformVersion: string | undefined
  brand: string | undefined
  model: string | undefined
}> => {
  const result = await _resultPromise

  return {
    platform: result.os.name?.toLowerCase(),
    platformVersion: result.os.version,
    brand: result.device.vendor,
    model: result.device.model,
  }
}

/**
 * Gather the common data for all events. If this fails,
 * throw an exception as we don't want to continue without
 * this data.
 */
export const useCommonRudderstackData = (): (() => Promise<Context>) => {
  const { customerSlug, eventSlug } = useConfigProperties()

  return useCallback(async () => {
    const env = getEnv()
    const deviceId = getDeviceID()
    const { platform, platformVersion } = await getPlatform()

    return {
      client: env.VITE_CLIENT ?? '',
      project: env.VITE_PROJECT ?? '',
      env: env.VITE_DEPLOY_ENV ?? '',
      product: 'eventsync',
      customer: customerSlug ?? '',
      debug: env.VITE_DEPLOY_ENV === 'dev' || !import.meta.env.PROD,
      spec_version: '0.1',
      app: {
        type: 'pwa',
        version: env.VITE_APP_VERSION ?? '',
        build: env.VITE_APP_RELEASE_VERSION || 'dev',
        name: eventSlug ?? '',
        role: 'byod',
      },
      device: {
        id: deviceId,
        os_version: platformVersion,
        os: platform?.toLocaleLowerCase() || 'unknown',
        time_zone: getTimeZone(),
        locale: navigator.language,
      },
    } satisfies Context
  }, [customerSlug, eventSlug])
}
